import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { useIsDesktop } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Member } from "../../Models/Member/Member.Current";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import { useTicketingStore } from "../../Models/Ticketing/Ticketing.Store";
import { CreateTicketActionSheet } from "./components/CreateTicketActionSheet";
import { CreateTicketModal } from "./components/CreateTicketModal";
import { TicketInbox } from "./components/TicketInbox";
import { TicketInboxNav } from "./components/TicketInboxNav";
import { TicketSearchView } from "./components/TicketSearchView";
import { TicketWorkspaceColumns } from "./components/TicketWorkspaceColumns";

export function TicketingPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const select = useTicketingStore((s) => s.selectTicket);
  const selectedTicketId = useTicketingStore((s) => s.selectedTicketId);
  const requestMyInboxView = useTicketingStore((s) => s.requestMyInboxView);
  const requestAllView = useTicketingStore((s) => s.requestAllView);
  const requestGroupView = useTicketingStore((s) => s.requestGroupView);
  const isDesktop = useIsDesktop();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isSearch = location.pathname.endsWith("/inbox/search");

  // Note: refreshInbox is handled by TicketInbox component with proper filters
  // Ticket socket events are subscribed globally on connect (SocketEvents.Manager.ts)
  useEffect(() => {
    NavBar.set(<NavBar.Component.Title title="Tickets" />);
  }, []);
  useEffect(() => {
    const raw = params.ticketNumber;
    if (!raw) {
      void select(null);
      return;
    }
    const num = Number(raw);
    if (!Number.isFinite(num)) return;
    void API.Ticketing.getTicketByNumber(Member.orgId, num)
      .then((res) => {
        select(res.ticket.id);
      })
      .catch((e: Error) => {
        const isNotFound = e.message === "TICKET_NOT_FOUND";
        useTicketingStore.setState(
          {
            selectedTicketId: -1,
            detailLoading: false,
            detailError: isNotFound ? "Sorry! This ticket cannot be found." : "Failed to load ticket",
            ticket: null
          },
          false,
          "ticketing/ticketByNumber:error"
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.ticketNumber]);

  const hasSelection = selectedTicketId !== null;

  // Search All — a full-width page: the reused left nav + the search view. Nav
  // clicks navigate back to /inbox and request the target view (the inbox's
  // local viewMode lives in TicketInbox, applied via the store one-shots).
  if (isSearch) {
    const goInbox = (fn: () => void): void => {
      fn();
      navigate("/inbox");
    };
    return (
      <div className="flex h-full min-h-0 gap-3 bg-grey-50 p-3">
        <div className="flex min-h-0 shrink-0 overflow-hidden rounded-lg border border-grey-200 bg-white">
          <TicketInboxNav
            viewMode="search"
            activeGroupSlug={null}
            onSelectView={(mode) => goInbox(mode === "my" ? requestMyInboxView : requestAllView)}
            onSelectGroup={(slug) => goInbox(() => requestGroupView(slug))}
            onSelectGroupInbox={() => goInbox(() => requestGroupView())}
            onSearchAll={() => undefined}
            onCreate={() => setShowCreateModal(true)}
            hideBorder
          />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-grey-200 bg-white">
          <TicketSearchView />
        </div>
        {showCreateModal && isDesktop && <CreateTicketModal onClose={() => setShowCreateModal(false)} />}
        {!isDesktop && <CreateTicketActionSheet isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />}
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 bg-grey-50 p-3 lg:grid-cols-[540px_1fr]">
      <div
        className={filterClasses(
          "flex min-h-0 flex-col overflow-hidden rounded-lg border border-grey-200 bg-white",
          hasSelection && "hidden lg:flex"
        )}
      >
        <TicketInbox />
      </div>

      <TicketWorkspaceColumns />
    </div>
  );
}
